import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";

import { API_BASE } from "../config";

export function ParametresCollectivite({ user }) {
  const token = user?.token;

  const [loading, setLoading] = useState(false);
  const [loadingPwd, setLoadingPwd] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Confirmation mot de passe (save profil)
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const confirmPwdRef = useRef(null);
  const [toast, setToast] = useState({ type: "", msg: "" });
  

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast({ type: "", msg: "" }), 3200);
  };

  const [profile, setProfile] = useState({
    id: "",
    nom: "",
    adresse: "",
    code_postal: "",
    ville: "",
    telephone: "",
    email_contact: "",
    SIRET: "",
  });

  const [form, setForm] = useState({
    nom: "",
    adresse: "",
    code_postal: "",
    ville: "",
    telephone: "",
    email_contact: "",
    SIRET: "",
  });

  const [pwd, setPwd] = useState({
    mot_de_passe_actuel: "",
    nouveau_mot_de_passe: "",
    confirmation: "",
  });

  const headers = useMemo(
    () => ({
      "Content-Type": "application/json",
      Authorization: `Bearer ${token || ""}`,
    }),
    [token]
  );

  const resetMessages = () => {
    setError("");
    setSuccess("");
  };

  const flashSuccess = (msg) => {
    setSuccess(msg);
    window.setTimeout(() => setSuccess(""), 2500);
  };

  const fetchProfil = useCallback(async () => {
    if (!token) return;
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/collectivite/profil`, { headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || "Erreur chargement profil");

      const p = {
        id: data.item?.id || "",
        nom: data.item?.nom || "",
        adresse: data.item?.adresse || "",
        code_postal: data.item?.code_postal || "",
        ville: data.item?.ville || "",
        telephone: data.item?.telephone || "",
        email_contact: data.item?.email_contact || "",
        SIRET: data.item?.SIRET || data.item?.siret || "",
      };

      setProfile(p);
      setForm({ ...p });

      setConfirmSaveOpen(false);
      setConfirmPassword("");
    } catch (e) {
      setError(e?.message || "Impossible de contacter le serveur.");
    } finally {
      setLoading(false);
    }
  }, [headers, token]);

  useEffect(() => {
    fetchProfil();
  }, [fetchProfil]);

  const isDirty =
    form.nom !== profile.nom ||
    form.adresse !== profile.adresse ||
    form.code_postal !== profile.code_postal ||
    form.ville !== profile.ville ||
    form.telephone !== profile.telephone ||
    form.email_contact !== profile.email_contact 

  const cancelEdits = () => {
    resetMessages();
    setForm({ ...profile });
    setConfirmSaveOpen(false);
    setConfirmPassword("");
  };

  const validateProfil = () => {
    if (!(form.nom || "").trim()) {
      setError("Nom de la collectivité requis.");
      return false;
    }
    if (!(form.adresse || "").trim()) {
      setError("Adresse requise.");
      return false;
    }
    if (!(form.ville || "").trim()) {
      setError("Ville requise.");
      return false;
    }
    if (!(form.telephone || "").trim()) {
      setError("Téléphone requis.");
      return false;
    }

    const email = (form.email_contact || "").trim();
    if (!email) {
      setError("Email de contact requis.");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Email de contact invalide.");
      return false;
    }

    const cp = (form.code_postal || "").trim();
    if (cp && !/^\d{5}$/.test(cp)) {
      setError("Code postal invalide (5 chiffres).");
      return false;
    }

    const siret = (form.SIRET || "").trim().replace(/\D/g, "");
    if (siret && siret.length !== 14) {
      setError("SIRET invalide (14 chiffres).");
      return false;
    }

    return true;
  };

  // Step 1: Enregistrer -> demande mot de passe
  const requestSave = () => {
    resetMessages();
    if (!isDirty) return;
    if (!validateProfil()) return;

    setConfirmSaveOpen(true);
    window.setTimeout(() => confirmPwdRef.current?.focus?.(), 50);
  };

  // Step 2: Confirmer -> PATCH
  const confirmAndSave = async () => {
    if (!token) return;
    resetMessages();
    if (!validateProfil()) return;

    const pwdValue = (confirmPassword || "").trim();
    if (!pwdValue) {
      setError("Veuillez saisir votre mot de passe pour valider.");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        nom: (form.nom || "").trim(),
        adresse: (form.adresse || "").trim(),
        code_postal: (form.code_postal || "").trim(),
        ville: (form.ville || "").trim(),
        telephone: (form.telephone || "").trim(),
        email_contact: (form.email_contact || "").trim().toLowerCase(),
        // ✅ on envoie les deux clés pour compat
        siret: (form.SIRET || "").trim().replace(/\D/g, ""),
        SIRET: (form.SIRET || "").trim().replace(/\D/g, ""),
        mot_de_passe: pwdValue, // si tu ajoutes le check côté backend
      };

      const res = await fetch(`${API_BASE}/collectivite/profil`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || "Erreur mise à jour");

      flashSuccess("Informations mise à jour");
      setConfirmSaveOpen(false);
      setConfirmPassword("");
      await fetchProfil();
    } catch (e) {
      setError(e?.message || "Impossible de contacter le serveur.");
    } finally {
      setLoading(false);
    }
  };

  // Sécurité
  const closePwd = () => {
    setPwd({ mot_de_passe_actuel: "", nouveau_mot_de_passe: "", confirmation: "" });
    setShowPasswordForm(false);
    resetMessages();
  };

  const validatePwd = () => {
    if (!pwd.mot_de_passe_actuel) {
      setError("Veuillez saisir votre mot de passe actuel.");
      return false;
    }
    if (!pwd.nouveau_mot_de_passe) {
      setError("Veuillez saisir un nouveau mot de passe.");
      return false;
    }
    if (pwd.nouveau_mot_de_passe.length < 8) {
      setError("Mot de passe trop court (min 8 caractères).");
      return false;
    }
    if (pwd.nouveau_mot_de_passe !== pwd.confirmation) {
      setError("La confirmation ne correspond pas.");
      return false;
    }
    return true;
  };

  const changePassword = async () => {
    if (!token) return;
    resetMessages();
    if (!validatePwd()) return;

    setLoadingPwd(true);
    try {
      // ✅ bonne route backend
      const res = await fetch(`${API_BASE}/collectivite/profil/password`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          mot_de_passe_actuel: pwd.mot_de_passe_actuel,
          nouveau_mot_de_passe: pwd.nouveau_mot_de_passe,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.msg || "Erreur changement mot de passe");

      flashSuccess("Mot de passe modifié");
      closePwd();
    } catch (e) {
      setError(e?.message || "Impossible de contacter le serveur.");
    } finally {
      setLoadingPwd(false);
    }
  };

  return (
    <div className="adminPanel blocagesCompact collectiviteSettings cabinetCompact">
      {error && <div className="adminError">{error}</div>}
      {success && <div className="adminSuccess">{success}</div>}

      {/* PROFIL */}
      <div className="adminCard adminCardSpan">
        <div className="adminCardHeader cabinetCardHeader">
        
          <button
          className="btnSecondary"
          onClick={async () => {
            await fetchProfil();
            showToast("success", "Profil est à jour");
          }}
          disabled={loading}
        >
            {loading ? "Chargement..." : "Rafraîchir"}
          </button>


        </div>

        <div className="cabinetForm">
          <label className="cabinetField">
            <div className="cabinetLabel">Nom de la collectivité</div>
            <input
              className="cabinetInput"
              value={form.nom}
              onChange={(e) => setForm((s) => ({ ...s, nom: e.target.value }))}
              placeholder="Ex: Ville de Paris"
            />
          </label>

          <div className="cabinetGrid2">
            <label className="cabinetField">
              <div className="cabinetLabel">Email de contact</div>
              <input
                className="cabinetInput"
                type="email"
                value={form.email_contact}
                onChange={(e) => setForm((s) => ({ ...s, email_contact: e.target.value }))}
                placeholder="contact@collectivite.ma"
                autoComplete="email"
              />
            </label>

            <label className="cabinetField">
              <div className="cabinetLabel">Téléphone</div>
              <input
                className="cabinetInput"
                value={form.telephone}
                onChange={(e) => setForm((s) => ({ ...s, telephone: e.target.value }))}
                placeholder="05 22 00 00 00"
                autoComplete="tel"
              />
            </label>
          </div>

          <label className="cabinetField">
            <div className="cabinetLabel">Adresse</div>
            <input
              className="cabinetInput"
              value={form.adresse}
              onChange={(e) => setForm((s) => ({ ...s, adresse: e.target.value }))}
              placeholder="Rue / Avenue / Numéro"
              autoComplete="street-address"
            />
          </label>

          <div className="cabinetGrid2">
            <label className="cabinetField">
              <div className="cabinetLabel">Code postal</div>
              <input
                className="cabinetInput"
                inputMode="numeric"
                value={form.code_postal}
                onChange={(e) =>
                  setForm((s) => ({ ...s, code_postal: e.target.value.replace(/\D/g, "").slice(0, 5) }))
                }
                placeholder="Ex: 20000"
                autoComplete="postal-code"
              />
            </label>

            <label className="cabinetField">
              <div className="cabinetLabel">Ville</div>
              <input
                className="cabinetInput"
                value={form.ville}
                onChange={(e) => setForm((s) => ({ ...s, ville: e.target.value }))}
                placeholder="Ex: Paris"
                autoComplete="address-level2"
              />
            </label>
          </div>

          <label className="cabinetField">
            <div className="cabinetLabel">SIRET</div>
            <input
              className="cabinetInput"
              inputMode="numeric"
              value={form.SIRET}
              disabled
              readOnly
            />
          </label>

          <div className="cabinetActions">
            <button className="btnSecondary btnCompact" onClick={cancelEdits} disabled={loading || !isDirty}>
              Annuler
            </button>

            <button className="btnPrimary btnCompact" onClick={requestSave} disabled={loading || !isDirty}>
              Enregistrer
            </button>
          </div>

          {confirmSaveOpen && (
            <div className="cabinetConfirmBox">
              <div className="cabinetConfirmTitle">Confirmer votre identité</div>
              <div className="cabinetConfirmSub">
                Pour valider ces modifications, saisissez votre mot de passe.
              </div>

              <label className="cabinetField" style={{ marginTop: 10 }}>
                <div className="cabinetLabel">Mot de passe</div>
                <input
                  ref={confirmPwdRef}
                  className="cabinetInput"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Votre mot de passe actuel"
                  autoComplete="current-password"
                />
              </label>

              <div className="cabinetActions" style={{ marginTop: 10 }}>
                <button
                  className="btnSecondary btnCompact"
                  onClick={() => {
                    setConfirmSaveOpen(false);
                    setConfirmPassword("");
                    resetMessages();
                  }}
                  disabled={loading}
                >
                  Annuler
                </button>

                <button className="btnPrimary btnCompact" onClick={confirmAndSave} disabled={loading}>
                  {loading ? "Validation..." : "Valider & enregistrer"}
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* SECURITE */}
      <div className="adminCard adminCardSpan">
        <div className="adminCardHeader cabinetCardHeader">
          <div>
            <div className="adminCardTitle">Sécurité</div>
            <div className="adminCardDesc">Changez votre mot de passe</div>
          </div>

          {!showPasswordForm ? (
            <button
              className="btnPrimary btnCompact"
              onClick={() => {
                resetMessages();
                setShowPasswordForm(true);
              }}
            >
              Changer le mot de passe
            </button>
          ) : (
            <button className="btnSecondary btnCompact" onClick={closePwd} disabled={loadingPwd}>
              Fermer
            </button>
          )}
        </div>

        <div className={`cabinetCollapse ${showPasswordForm ? "isOpen" : ""}`}>
          <div className="cabinetPwdGrid">
            <label className="cabinetField">
              <div className="cabinetLabel">Mot de passe actuel</div>
              <input
                className="cabinetInput"
                type="password"
                value={pwd.mot_de_passe_actuel}
                onChange={(e) => setPwd((s) => ({ ...s, mot_de_passe_actuel: e.target.value }))}
                placeholder="Votre mot de passe actuel"
                autoComplete="current-password"
              />
            </label>

            <label className="cabinetField">
              <div className="cabinetLabel">Nouveau mot de passe</div>
              <input
                className="cabinetInput"
                type="password"
                value={pwd.nouveau_mot_de_passe}
                onChange={(e) => setPwd((s) => ({ ...s, nouveau_mot_de_passe: e.target.value }))}
                placeholder="Minimum 8 caractères"
                autoComplete="new-password"
              />
            </label>

            <label className="cabinetField">
              <div className="cabinetLabel">Confirmation</div>
              <input
                className="cabinetInput"
                type="password"
                value={pwd.confirmation}
                onChange={(e) => setPwd((s) => ({ ...s, confirmation: e.target.value }))}
                placeholder="Retapez le nouveau mot de passe"
                autoComplete="new-password"
              />
            </label>
          </div>

          <div className="cabinetActions">
            <button className="btnSecondary btnCompact" onClick={closePwd} disabled={loadingPwd}>
              Annuler
            </button>

            <button className="btnPrimary btnCompact" onClick={changePassword} disabled={loadingPwd}>
              {loadingPwd ? "Modification..." : "Enregistrer"}
            </button>
          </div>

          <div className="cabinetHint">
            Conseil : utilisez au moins 8 caractères, avec lettres et chiffres.
          </div>
        </div>
      </div>
      {toast.msg && (
  <div className={`adminToast ${toast.type}`}>
    {toast.msg}
  </div>
)}
    </div>

  );
}
